import { useEffect, useState } from "react";
import { Loader2, Save, Rocket, AlertCircle, CheckCircle2, Eye, X, FileText } from "lucide-react";

/**
 * Feb-2026 (User-Wunsch UX) — Sticky-Submit-Leiste am unteren Bildschirmrand.
 *
 * Zeigt während des Scrollens immer den Live-Pflichtfeld-Counter sowie die
 * vollen Aktionen — identisch zur FooterActionsSection unten:
 *  • Abbrechen
 *  • Vorschau
 *  • Als Entwurf speichern  (Pflichtfeld-frei → Zwischenspeichern)
 *  • Ankauf erstellen        (B2B-only, generiert PDF)
 *  • Veröffentlichen / Änderungen speichern
 * Verschwindet, sobald die FooterActionsSection im Viewport ist (kein Doppel-CTA).
 */
export default function StickyActionBar({
    missingCount,
    onSaveDraft,
    onSubmit,
    onCancel,
    onPreview,
    onAnkauf,
    ankaufLoading,
    isDealer,
    submitting,
    isEdit,
    savingDraft,
    footerRef,
}) {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            if (!footerRef?.current) {
                setVisible(true);
                return;
            }
            const r = footerRef.current.getBoundingClientRect();
            // Wenn FooterActionsSection NICHT im Viewport ist, Sticky-Bar zeigen.
            const inView = r.top < window.innerHeight - 40 && r.bottom > 80;
            setVisible(!inView);
        };
        handleScroll();
        window.addEventListener("scroll", handleScroll, { passive: true });
        window.addEventListener("resize", handleScroll);
        return () => {
            window.removeEventListener("scroll", handleScroll);
            window.removeEventListener("resize", handleScroll);
        };
    }, [footerRef]);

    if (!visible) return null;

    const ready = missingCount === 0;
    // Feb-2026 (User-Wunsch 24 May) — Smart-Primary-CTA:
    // Sind Pflichtfelder offen, ist „Entwurf speichern" der sinnvolle Default,
    // weil „Veröffentlichen" sonst sofort den Pflichtfeld-Toast feuert.
    // Wir blenden den separaten gelben Draft-Button aus und färben den
    // Primary-Button um, damit nichts doppelt erscheint.
    const primaryActsAsDraft = !ready && !isEdit;

    return (
        <div
            className="fixed inset-x-0 bottom-0 z-30 bg-white border-t-2 border-[#111827] shadow-2xl"
            data-testid="sticky-action-bar"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                    {ready ? (
                        <>
                            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                            <span className="font-bold text-sm text-emerald-700">
                                Alle Pflichtfelder ausgefüllt — bereit zum Veröffentlichen
                            </span>
                        </>
                    ) : (
                        <>
                            <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
                            <span className="font-bold text-sm text-[#111827]" data-testid="sticky-missing-count">
                                {missingCount} {missingCount === 1 ? "Feld fehlt" : "Felder fehlen"} noch
                            </span>
                        </>
                    )}
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                    {onCancel && (
                        <button
                            type="button"
                            onClick={onCancel}
                            className="h-9 px-3 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider border border-gray-300 text-gray-700 rounded-sm hover:bg-gray-50"
                            data-testid="sticky-cancel-btn"
                        >
                            <X className="w-3.5 h-3.5" />
                            Abbrechen
                        </button>
                    )}
                    {onPreview && (
                        <button
                            type="button"
                            onClick={onPreview}
                            className="h-9 px-3 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider border border-[#111827] text-[#111827] rounded-sm hover:bg-gray-50"
                            data-testid="sticky-preview-btn"
                        >
                            <Eye className="w-3.5 h-3.5" />
                            Vorschau
                        </button>
                    )}
                    {!isEdit && (
                        <button
                            type="button"
                            onClick={onSaveDraft}
                            disabled={savingDraft || submitting}
                            className={`h-9 ${primaryActsAsDraft ? "px-4" : "px-3"} inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-[#FFCC00] text-[#111827] rounded-sm hover:bg-yellow-300 disabled:opacity-50`}
                            data-testid="sticky-save-draft-btn"
                            title={primaryActsAsDraft ? "Pflichtfelder fehlen — speichert als Entwurf, du kannst später weiter befüllen" : "Zwischenspeichern — auch ohne ausgefüllte Pflichtfelder möglich"}
                        >
                            {savingDraft ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                            💾 Als Entwurf speichern
                        </button>
                    )}
                    {isDealer && onAnkauf && (
                        <button
                            type="button"
                            onClick={onAnkauf}
                            disabled={!!ankaufLoading}
                            className="h-9 px-3 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm disabled:opacity-50"
                            data-testid="sticky-ankauf-btn"
                            title="Verbindlichen Kfz-Ankaufvertrag als PDF generieren"
                        >
                            {ankaufLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
                            Ankauf erstellen
                        </button>
                    )}
                    {!primaryActsAsDraft && (
                        <button
                            type="button"
                            onClick={onSubmit}
                            disabled={submitting}
                            className="h-9 px-4 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-[#111827] text-white rounded-sm hover:bg-black disabled:bg-gray-200 disabled:text-gray-400"
                            data-testid="sticky-submit-btn"
                        >
                            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Rocket className="w-3.5 h-3.5" />}
                            {isEdit ? "Änderungen speichern" : "Veröffentlichen"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
